import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";

import LocalizedReviewTitleAndRating from "components/flexComponents/HotelRecentReviews/components/ReviewTitleAndRating";
import { LocalizedReviewAuthorAndPostedDate } from "components/flexComponents/HotelRecentReviews/components/ReviewAuthorAndPostedDate";
import { LocalizedReviewCardLink } from "components/flexComponents/HotelRecentReviews/components/ReviewLink";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { withStores } from "src/stores";

import {
  HotelRecentReviewsFlexModuleResult,
  HotelReview,
} from "typings/microserviceModels/hotel-recent-reviews-flex-module";
import { HotelRecentReviewsProps } from "./typings";
import { observer } from "mobx-react";
import { LazyLoad } from "components/utility/LazyLoad/LazyLoad";

export const HotelRecentReviews = withStores(
  "analytics",
  "context",
  "flexModuleModelStore"
)(
  observer((props: HotelRecentReviewsProps) => {
    const { flexTemplateNode, flexModuleModelStore, context } = props;
    const { id, options } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as HotelRecentReviewsFlexModuleResult | null;
    const keyHelper = new ItemKeyHelper("hotel-recent-reviews");

    if (!model || !model.hotelReviewList || model.hotelReviewList.length === 0) {
      return null;
    }

    const { title, hotelReviewList } = model;
    const { formatText } = useLocalization();

    return (
      <LazyLoad context={context}>
        <div
          className="HotelRecentReviews all-t-padding-three all-b-padding-three all-cell-1-1 uitk-cell"
          id={id}
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
        >
          <div className="carouselMediumContainer">
            <UitkHeading level={2} size={600}>
              {title}
            </UitkHeading>
            <UitkSpacing margin={{ blockstart: "two" }}>
              <IsomorphicCarousel
                itemsVisible={{ sm: 1, md: 1, lg: 2 }}
                pageBy={1}
                peekWidth={11}
                buttonText={{
                  nextButton: formatText("carousel.item.next"),
                  prevButton: formatText("carousel.item.prev"),
                }}
              >
                {hotelReviewList.map((hotel, hotelIndex) => (
                  <HotelRecentReviewCarouselCard
                    key={keyHelper.next()}
                    hotelRecentReview={hotel}
                    index={hotelIndex}
                    showReviewSuperlative={model.showReviewSuperlative}
                  />
                ))}
              </IsomorphicCarousel>
            </UitkSpacing>
          </div>
        </div>
      </LazyLoad>
    );
  })
);

const ReviewCardContent = ({
  hotelReview,
  index,
  showReviewSuperlative,
}: {
  hotelReview: HotelReview;
  index: number;
  showReviewSuperlative: boolean;
}) => (
  <UitkCardAlohaContentSection>
    <LocalizedReviewTitleAndRating
      rating={hotelReview.rating}
      reviewTitle={hotelReview.reviewTitle}
      showReviewSuperlative={showReviewSuperlative}
    />
    <ReviewText reviewText={hotelReview.reviewText} />
    <LocalizedReviewAuthorAndPostedDate
      authorName={hotelReview.authorName}
      authorLocation={hotelReview.authorLocation}
      datePosted={hotelReview.date}
      hotelName={hotelReview.hotelName}
      index={index}
    />
    <LocalizedReviewCardLink
      index={index}
      hotelName={hotelReview.hotelName}
      hotelInfositeUrl={hotelReview.hotelInfositeUrl}
    />
  </UitkCardAlohaContentSection>
);

const ReviewText = ({ reviewText }: { reviewText: string }) => (
  <UitkSpacing margin={{ block: "one" }}>
    <UitkText overflow="truncate-2-lines" size={300}>
      {reviewText}
    </UitkText>
  </UitkSpacing>
);

const HotelRecentReviewCarouselCard = ({
  hotelRecentReview,
  index,
  showReviewSuperlative,
}: {
  hotelRecentReview: HotelReview;
  index: number;
  showReviewSuperlative: boolean;
}) => {
  return (
    <Viewport>
      <ViewSmall>
        <UitkCardAloha className="HotelRecentReviewsCard" border>
          <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
            <LazyLoadedImage src={hotelRecentReview.hotelImageUrl} alt={hotelRecentReview.hotelName} />
          </UitkFigure>
          <ReviewCardContent
            hotelReview={hotelRecentReview}
            index={index + 1}
            showReviewSuperlative={showReviewSuperlative}
          />
        </UitkCardAloha>
      </ViewSmall>

      <ViewMedium>
        <UitkCardAloha className="HotelRecentReviewsCard">
          <UitkLayoutFlex>
            <UitkLayoutFlexItem minWidth={160} shrink={0}>
              <div>
                <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} className="all-image-fit-cover">
                  <LazyLoadedImage src={hotelRecentReview.hotelImageUrl} alt={hotelRecentReview.hotelName} />
                </UitkFigure>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={1}>
              <ReviewCardContent
                hotelReview={hotelRecentReview}
                index={index}
                showReviewSuperlative={showReviewSuperlative}
              />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkCardAloha>
      </ViewMedium>
    </Viewport>
  );
};

export default HotelRecentReviews;
